import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Trash2, KeyRound } from "lucide-react";
import { api } from "@/lib/api";

export default function AdminSettings() {
  const [settings, setSettings] = useState({ configured: false, api_key_masked: "" });
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings/uploadpost");
      setSettings(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!apiKey.trim()) return toast.error("API key is required");
    setSaving(true);
    try {
      await api.put("/admin/settings/uploadpost", { api_key: apiKey.trim() });
      toast.success("Shared publishing key saved. All users can now add TikTok accounts.");
      setApiKey("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove the shared publishing key? Users won't be able to add or link TikTok accounts until you set a new one.")) return;
    try {
      await api.delete("/admin/settings/uploadpost");
      toast.success("Shared key removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <header>
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Admin</div>
        <h1 className="mt-1 font-display font-black tracking-tight text-2xl sm:text-4xl lg:text-5xl text-slate-900">Settings</h1>
        <p className="mt-1.5 text-sm sm:text-base text-slate-600">Shared publishing key used by every PostQik user.</p>
      </header>

      <Card className="p-4 sm:p-6 border-zinc-200">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-slate-700" />
              <div className="font-display text-2xl font-bold text-slate-900">TikTok publishing key</div>
            </div>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Enter your master Upload-Post API key. Every PostQik user (including testers) will auto-provision their own profile on your Upload-Post account when they click <span className="font-medium">Add TikTok account</span>. They never see or type a key.
            </p>
          </div>
          {settings.configured ? (
            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><CheckCircle2 className="w-3.5 h-3.5" /> Set</Badge>
          ) : (
            <Badge variant="secondary" className="bg-zinc-100 text-zinc-700"><XCircle className="w-3.5 h-3.5" /> Not set</Badge>
          )}
        </div>

        <form onSubmit={save} className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <Label htmlFor="master-key" className="text-slate-700">Master API key</Label>
            <Input
              id="master-key"
              type="password"
              autoComplete="off"
              placeholder={settings.configured ? `Update key (current ${settings.api_key_masked || "•••"})` : "Apikey_xxxxxxxx"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              data-testid="master-key-input"
            />
            <p className="mt-1 text-xs text-slate-500">Stored server-side and never exposed to any user&apos;s browser.</p>
          </div>
          <Button type="submit" disabled={saving || loading} className="bg-zinc-900 hover:bg-zinc-800 text-white h-9" data-testid="save-master-key">
            {saving ? "Saving…" : settings.configured ? "Update key" : "Save key"}
          </Button>
        </form>

        {settings.configured && (
          <div className="mt-4">
            <Button type="button" variant="ghost" onClick={remove} className="text-slate-500 text-xs" data-testid="delete-master-key">
              <Trash2 className="w-3.5 h-3.5" /> Remove shared key
            </Button>
          </div>
        )}

        {settings.updated_at && (
          <div className="mt-4 text-xs text-slate-500 tabular-nums">
            Last updated {new Date(settings.updated_at).toLocaleString()}
          </div>
        )}
      </Card>
    </div>
  );
}
